import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/PasswordInput'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMemo } from 'react'
import { useResetPasswordMutation } from '@/hooks/useAuth'
import { toast } from 'sonner'

const schemaBase = z
  .object({
    token: z.string().min(10, 'Token requis'),
    newPassword: z.string().min(8, 'Min 8 caractères'),
    confirm: z.string().min(8, 'Min 8 caractères'),
  })
  .refine((v) => v.newPassword === v.confirm, {
    path: ['confirm'],
    message: 'Les mots de passe ne correspondent pas',
  })

type FormValues = z.infer<typeof schemaBase>

export default function ResetPassword() {
  const { search } = useLocation()
  const navigate = useNavigate()
  const tokenFromUrl = useMemo(
    () => new URLSearchParams(search).get('token') ?? '',
    [search]
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(schemaBase),
    defaultValues: { token: tokenFromUrl, newPassword: '', confirm: '' },
  })

  const { mutateAsync, isPending } = useResetPasswordMutation()

  async function onSubmit(values: FormValues) {
    try {
      await mutateAsync({
        token: values.token,
        newPassword: values.newPassword,
      })
      toast.success('Mot de passe mis à jour. Vous pouvez vous connecter.')
      navigate('/login')
    } catch (e: any) {
      toast.error('Lien invalide ou expiré.', {
        description: 'Refaites la demande de réinitialisation.',
      })
    }
  }

  return (
    <div className='flex min-h-[100vh] items-center justify-center bg-gradient-to-br from-[hsl(0,0%,100%)] via-[hsl(140,13%,91%)] to-[hsl(144,80%,73%)]'>
      <Card className='w-[520px] shadow-2xl rounded-xl'>
        <CardHeader>
          <h1 className='text-2xl font-semibold'>
            Réinitialiser le mot de passe
          </h1>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
              <FormField
                control={form.control}
                name='token'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Token</FormLabel>
                    <FormControl>
                      <Input placeholder='Coller le token' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='newPassword'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nouveau mot de passe</FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder='Min 8 caractères'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='confirm'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmer</FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder='Répétez le mot de passe'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type='submit' disabled={isPending}>
                {isPending ? 'Validation…' : 'Réinitialiser'}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className='justify-center text-sm text-muted-foreground'>
          <a href='/forgot-password' className='text-primary hover:underline'>
            Refaire une demande
          </a>
        </CardFooter>
      </Card>
    </div>
  )
}
